import { useEffect } from 'react';

/**
 * Hält die Seite hinter einem offenen Overlay still.
 *
 * 🚨 WARUM ES DAS BRAUCHT — gemessen am 13.09.2026 auf der laufenden Website:
 * Bei geöffnetem Dialog ließ sich die Seite dahinter um 400 px scrollen,
 * `body` und `html` standen beide auf `overflow: visible`. Ein natives
 * `<dialog>` macht den Hintergrund per `inert` UNBEDIENBAR, aber nicht
 * UNBEWEGLICH — das sind zwei verschiedene Dinge, und der Browser übernimmt
 * nur das erste. Tils Auftrag lautete: „Alle Dialoge, PopUps und Fenster
 * sollten fest stehen."
 *
 * 🚨 WARUM `overflow: hidden` ALLEIN NICHT GENÜGT — und das ist der Punkt, an
 * dem die naheliegende Fassung auf genau dem Gerät versagt, um das es geht.
 * Auf dem iPhone hält Safari sich nicht zuverlässig an ein `overflow: hidden`
 * am `body`: die Seite lässt sich mit dem Finger weiterschieben. Belastbar ist
 * dort nur, den `body` aus dem Fluss zu nehmen (`position: fixed`) und ihn um
 * die aktuelle Scrollhöhe nach oben zu versetzen — dann steht wirklich alles.
 * Beim Freigeben wird die Höhe zurückgesprungen, sonst landet der Leser nach
 * jedem Dialog wieder ganz oben.
 *
 * Vier Dinge, die eine naive Fassung falsch macht:
 *
 *  1. **Sie zählt nicht mit.** Liegt ein Dialog über einer Schublade und wird
 *     der obere geschlossen, gäbe eine naive Fassung die Sperre frei, obwohl
 *     die Schublade noch offen ist. Deshalb ein Zähler über ALLE Overlays —
 *     erst das letzte gibt frei.
 *  2. **Sie setzt beim Aufräumen `''` statt des vorherigen Werts.** Stand dort
 *     vorher etwas anderes, ist es danach weg. Deshalb wird der alte Zustand
 *     gemerkt und vollständig zurückgeschrieben.
 *  3. **Sie vergisst die Scrollhöhe.** Ohne `window.scrollTo` danach springt
 *     der Leser bei jedem Schließen an den Seitenanfang.
 *  4. **Sie vergisst die Bildlaufleiste.** Am Rechner nimmt die Sperre die
 *     Leiste weg, die Seite wird dadurch breiter und springt sichtbar zur
 *     Seite. Deshalb wird ihre Breite als Innenabstand ausgeglichen. Am
 *     Telefon ist die Differenz 0 und es passiert nichts.
 *
 * ⚠️ Dieser Haken fasst AUSSCHLIESSLICH `overflow`, `position`, `top`, `width`
 * und `paddingRight` an — niemals `paddingBottom`. Den schreibt allein die
 * Backoffice-Leiste (`AdminBottomNav`), und zwei Schreiber auf demselben Feld
 * löschen sich gegenseitig aus: das Aufräumen des einen nimmt die Reservierung
 * des anderen mit. Dieselbe Regel steht als Zusage an `KassierenLeiste.tsx`.
 */

/** Wie viele Overlays sind gerade offen? Modul-global, über alle Instanzen. */
let offeneOverlays = 0;

/** Der Zustand VOR der ersten Sperre — nur dieser wird zurückgeschrieben. */
let vorher: {
  overflow: string;
  position: string;
  top: string;
  width: string;
  paddingRight: string;
  scrollY: number;
} | null = null;

function sperren(): void {
  offeneOverlays += 1;
  if (offeneOverlays > 1) return; // schon gesperrt, nichts zu tun

  const body = document.body;
  const scrollY = window.scrollY;

  vorher = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
    paddingRight: body.style.paddingRight,
    scrollY,
  };

  // Die Breite der Bildlaufleiste, BEVOR sie verschwindet. Am Telefon 0.
  const leiste = window.innerWidth - document.documentElement.clientWidth;

  body.style.overflow = 'hidden';
  // Der iPhone-taugliche Teil: den Fluss verlassen und die Scrollhöhe
  // ausgleichen, damit optisch nichts springt.
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.width = '100%';

  if (leiste > 0) {
    const bisher = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${bisher + leiste}px`;
  }
}

function freigeben(): void {
  offeneOverlays = Math.max(0, offeneOverlays - 1);
  if (offeneOverlays > 0 || vorher === null) return; // noch etwas offen

  const body = document.body;
  const { overflow, position, top, width, paddingRight, scrollY } = vorher;
  vorher = null;

  body.style.overflow = overflow;
  body.style.position = position;
  body.style.top = top;
  body.style.width = width;
  body.style.paddingRight = paddingRight;

  // Erst nach dem Zurücksetzen springen — vorher ist die Seite nicht scrollbar.
  window.scrollTo(0, scrollY);
}

/**
 * Sperrt den Hintergrund, solange `aktiv` wahr ist.
 *
 * Aufräumen läuft auch, wenn die Komponente im offenen Zustand ausgehängt wird
 * — sonst bliebe die Seite für immer starr.
 */
export function useScrollSperre(aktiv: boolean): void {
  useEffect(() => {
    if (!aktiv) return;
    sperren();
    return freigeben;
  }, [aktiv]);
}

/** Nur für Prüfungen: den Zähler zurücksetzen. Im Betrieb nie aufgerufen. */
export function __sperreZuruecksetzenFuerTests(): void {
  offeneOverlays = 0;
  vorher = null;
}
